/**
 * M11: the galaxy map – a premium 2D view built from reusable, baked textures.
 *
 *  - procedural, deterministic deep-space backdrop (pixi/map-backdrop.ts), independent of game data
 *  - stars are Sprites sharing one baked texture per star type (corona, black-hole accretion disk, binary pair …)
 *  - three levels of detail (graphics/lod.ts): galaxy / region / close, with hysteresis
 *  - territories: soft owner-coloured halos + owner-coloured lanes inside a civilisation
 *  - fleets: the shared ship art (graphics/ship-art.ts) as sprites; traveling ships get an engine trail
 *  - retained mode: nothing is recreated per frame; the loop renders only when something changed or animates
 *
 * Fog of war: the renderer only ever receives what the server already allows the player to know
 * (`systems`, `lanes`, own `fleets`) and draws exactly that. The background never uses game data.
 */
import * as PIXI from 'pixi.js';
// M10.6: the production CSP has no 'unsafe-eval'; this swaps Pixi's generated code for static paths
import 'pixi.js/unsafe-eval';
import { formatLy, type StarSystemClient, type StarLaneClient, type FleetClient } from '@galaxis/shared';
import { categoryOf, fleetsBySystem, type FleetCategory } from '../components/fleets/fleet-category.ts';
import { formatDuration } from '../components/fleets/fleet-format.ts';
import { fanSlots, fleetsInRect, hitTest, rectFrom, shouldStack, FLEET_HIT_RADIUS, type HitKind, type HitTarget, type Offset } from './fleet-hit.ts';
import { ACCENT, ROLE_COLORS, hexOf, starStyle, type Role } from '../design/tokens.ts';
import { ArtAtlas, STAR_TEX_R } from '../graphics/pixi-art.ts';
import { ART_BOX, representativeClass, type ShipClass, designOf } from '../graphics/ship-art.ts';
import { LOD_SHIP_PX, LOD_STAR_MUL, MIN_STAR_GLOW_PX, SpatialGrid, inRect, lodFor, visibleWorldRect, type Lod } from '../graphics/lod.ts';
import { QUALITY_SETTINGS, currentQuality, prefersReducedMotion, type Quality } from '../graphics/quality.ts';
import { MapBackdrop } from './map-backdrop.ts';
import { CameraGestures, zoomAt, type Camera } from './camera-gestures.ts';

export interface GalaxyMapCallbacks {
  /** A star was tapped (or, in move mode, chosen as the destination). */
  onSelectSystem: (id: string | null) => void;
  /** An own fleet's marker was tapped; `additive` = Ctrl / Shift held. */
  onFleetTap?: (fleetId: string, systemId: string, additive: boolean) => void;
  /** A foreign fleet's marker (only ever drawn for fleets the server listed) was tapped. */
  onForeignTap?: (fleetId: string, systemId: string) => void;
  /** A counted stack of fleets was tapped: it opens. */
  onStackTap?: (systemId: string) => void;
  /** Empty space was tapped. */
  onEmptyTap?: () => void;
  /** A box was dragged (Shift + drag, mouse): the own fleets inside. */
  onBoxSelect?: (fleetIds: string[], additive: boolean) => void;
  /** Right mouse button on a system (the Stellaris-style move command). */
  onContextTarget?: (systemId: string) => void;
  /** Mouse hover over a fleet marker (tooltip), null when it leaves. */
  onHover?: (info: { fleetId: string; kind: HitKind; x: number; y: number } | null) => void;
}

/** A foreign fleet the server showed to the player (name = owner civilization, shipCounts by class). */
export interface ForeignFleetMarker { id: string; name: string; color: number; shipCounts: Record<string, number> }

const CATEGORY_ROLE: Record<FleetCategory, Role> = {
  survey: 'survey', construction: 'build', military: 'military', mixed: 'diplomacy', empty: 'neutral',
};

const SHIP_TEX_H = ART_BOX.h * 32; // logical px height of a baked ship texture
const MIN_SCALE = 0.12;
const MAX_SCALE = 5;
/** While a gesture is running the heavy rebuilds happen at most this often; the camera itself moves every frame. */
const HEAVY_GAP_MS = 110;

interface StarNode {
  sys: StarSystemClient;
  star: PIXI.Sprite;
  fogGlow: PIXI.Sprite | null;
  halo: PIXI.Sprite | null;
  ownerRing: PIXI.Sprite | null;
  homeRing: PIXI.Sprite | null;
  buildBadge: PIXI.Text | null;
  ownerColor: number | null;
  state: string;
  type: string;
}

interface MarkerNode {
  container: PIXI.Container; ship: PIXI.Sprite; trail: PIXI.Sprite | null; badge: PIXI.Container | null;
  key: string; kind: HitKind; fleetId: string | null; systemId: string;
  /** screen offset (CSS px) from the star */
  slot: Offset; sizeFactor: number;
}

interface TravelNode {
  fleetId: string;
  container: PIXI.Container;
  ship: PIXI.Sprite;
  trail: PIXI.Sprite;
  from: { x: number; y: number };
  to: { x: number; y: number };
  start: number;
  end: number;
}

/** M10.6: the mounted map, for the bug-report snapshot. */
let currentMap: GalaxyMapRenderer | null = null;

/** PNG data URL of the game map only (no page content), longest side ≤ maxSide px; null without a map. */
export async function captureGalaxyMap(maxSide = 1280): Promise<string | null> {
  return currentMap ? currentMap.snapshot(maxSide) : null;
}

export class GalaxyMapRenderer {
  private constructionSystems = new Set<string>();
  private app: PIXI.Application;
  private viewport = new PIXI.Container();
  private lanesGfx = new PIXI.Graphics();
  /** unknown–unknown lanes live in their own Graphics: one dim batch, independent of the lit lanes */
  private faintGfx = new PIXI.Graphics();
  private flashes: Array<{ sprite: PIXI.Sprite; at: number; x: number; y: number }> = [];
  /** Length labels (ly) of the lanes touching the selected system; shown on selection, so they work on touch too. */
  private laneLabelPool: PIXI.Text[] = [];
  private routeGfx = new PIXI.Graphics();
  private travelLines = new PIXI.Graphics();
  private haloLayer = new PIXI.Container();
  private starLayer = new PIXI.Container();
  private markLayer = new PIXI.Container();
  private fleetLayer = new PIXI.Container();
  private travelLayer = new PIXI.Container();
  private selectLayer = new PIXI.Container();
  private labelLayer = new PIXI.Container();

  private atlas!: ArtAtlas;
  private backdrop!: MapBackdrop;
  private quality: Quality = 'medium';
  private reduced = false;

  private systems: StarSystemClient[] = [];
  private lanes: StarLaneClient[] = [];
  private fleets: FleetClient[] = [];
  private nodes = new Map<string, StarNode>();
  private systemMap = new Map<string, StarSystemClient>();
  private grid = new SpatialGrid<{ x: number; y: number; id: string }>(48);
  private selectedId: string | null = null;
  private selectedFleetIds = new Set<string>();
  private fleetById = new Map<string, FleetClient>();
  private foreign: { systemId: string; fleets: ForeignFleetMarker[] } | null = null;
  private expandedStack: string | null = null;
  private moveMode = false;
  /** Ctrl / Cmd held on the pointerdown that may become a tap: the tap adds to the selection instead of replacing it */
  private tapAdditive = false;
  /** Shift + drag with the mouse: a selection box (screen px) */
  private box: { id: number; ax: number; ay: number; moved: boolean } | null = null;
  private hoverKey: string | null = null;
  private fxGfx = new PIXI.Graphics();
  private boxGfx = new PIXI.Graphics();
  private fleetLabelLayer = new PIXI.Container();
  private fleetLabels = new Map<string, PIXI.Text>();
  private lastFleetLabelAt = 0;
  private callbacks: GalaxyMapCallbacks;
  private routePath: string[] = [];

  private markers = new Map<string, MarkerNode>();
  private travelers = new Map<string, TravelNode>();
  private labelPool: PIXI.Text[] = [];
  private badgePool: PIXI.Container[] = [];
  private selectRing: PIXI.Sprite | null = null;
  private selectBrackets: PIXI.Sprite | null = null;
  private selectGlow: PIXI.Sprite | null = null;

  // camera / interaction
  private lod: Lod = 'region';
  private lastScaledAt = 0;
  private gestures: CameraGestures;
  /** heavy camera work (lane / label / star-scale rebuilds) is rate-limited while a finger is down */
  private gestureActive = false;
  private camDirty = false;
  private lastHeavyAt = 0;
  private domCleanup: Array<() => void> = [];

  // render loop
  private raf: number | null = null;
  private dirty = true;
  private labelsDirty = true;
  private lanesDirty = true;
  private lastLabelAt = 0;
  private lastPulseAt = 0;
  private lastFogAt = 0;
  private fogCount = 0;

  private worldSize = 1000;
  private initialized = false;
  private destroyed = false;
  private cameraFramed = false;
  private screenW = 0;
  private screenH = 0;

  /** Counters for diagnostics / the performance test (no per-frame allocations are expected). */
  readonly stats = { frames: 0, renders: 0, labelRebuilds: 0, laneRebuilds: 0 };

  constructor(callbacks: GalaxyMapCallbacks) {
    this.callbacks = callbacks;
    this.gestures = new CameraGestures({
      getCamera: () => this.cameraState(),
      setCamera: cam => this.applyCamera(cam),
      onTap: p => { const add = this.tapAdditive; this.tapAdditive = false; this.tapAt(p.x, p.y, add); },
      onActiveChange: active => this.setGestureActive(active),
      minScale: MIN_SCALE,
      maxScale: MAX_SCALE,
    });
    this.app = new PIXI.Application();
    this.viewport.addChild(this.faintGfx, this.lanesGfx, this.routeGfx, this.haloLayer, this.starLayer, this.markLayer, this.travelLines, this.fleetLayer, this.travelLayer, this.selectLayer, this.fxGfx, this.fleetLabelLayer, this.labelLayer);
  }

  async init(container: HTMLDivElement): Promise<void> {
    if (this.destroyed) return;
    this.quality = currentQuality();
    this.reduced = prefersReducedMotion();
    const q = QUALITY_SETTINGS[this.quality];

    await this.app.init({
      resizeTo: container,
      background: 0x030712,
      antialias: q.antialias,
      resolution: Math.min(window.devicePixelRatio || 1, q.maxResolution),
      autoDensity: true,
      autoStart: false, // our loop renders only when something changed or moves
    });

    if (this.destroyed) {
      // init() completed but destroy() was called while awaiting — clean up properly
      try { this.app.destroy(true); } catch { /* ignore */ }
      return;
    }

    this.initialized = true;
    // eslint-disable-next-line @typescript-eslint/no-this-alias -- module-level handle for the bug-report snapshot
    currentMap = this;
    container.appendChild(this.app.canvas as HTMLCanvasElement);
    (this.app.canvas as HTMLCanvasElement).style.cssText = 'display:block;width:100%;height:100%;touch-action:none;';

    this.atlas = new ArtAtlas(this.app.renderer, this.quality);
    this.screenW = this.app.screen.width;
    this.screenH = this.app.screen.height;
    this.backdrop = new MapBackdrop(this.app.renderer, this.atlas, this.quality, this.screenW, this.screenH);
    this.app.stage.addChild(this.backdrop.screenLayer);
    this.viewport.addChildAt(this.backdrop.worldLayer, 0);
    this.app.stage.addChild(this.viewport);
    this.app.stage.addChild(this.boxGfx);

    this.fitGalaxyInView();
    this.setupInteraction();
    this.app.renderer.on('resize', () => this.onResize());
    this.lod = lodFor(this.viewport.scale.x);
    this.cameraChanged();
    // E2E tests locate stars on the canvas through this dev-only handle
    if (import.meta.env.DEV) (window as unknown as { __galaxisMap?: GalaxyMapRenderer }).__galaxisMap = this;
    // data may have arrived before the renderer was ready
    if (this.systems.length) this.rebuildNodes();
    this.requestRender();
  }

  // ---------------------------------------------------------------- camera

  /** Pans (keeping zoom, at least 0.8) so the system sits in the middle of the view. */
  centerOn(systemId: string): void {
    const s = this.systemMap.get(systemId);
    if (!s || !this.initialized) return;
    const scale = Math.max(this.viewport.scale.x, 0.8);
    this.viewport.scale.set(scale);
    const { width, height } = this.app.screen;
    // On phones/tablets the system panel opens as a bottom sheet: keep the system in the visible top part
    const focusY = width < 1024 ? height * 0.27 : height / 2;
    this.viewport.x = width / 2 - s.x * scale;
    this.viewport.y = focusY - s.y * scale;
    this.cameraFramed = true;
    this.cameraChanged();
    this.renderNow();
  }

  screenPositionOf(systemId: string): { x: number; y: number } | null {
    const s = this.systemMap.get(systemId) ?? this.systems.find(sys => sys.id === systemId);
    if (!s) return null;
    return { x: this.viewport.x + s.x * this.viewport.scale.x, y: this.viewport.y + s.y * this.viewport.scale.y };
  }

  private fitGalaxyInView(): void {
    const { width, height } = this.app.screen;
    const padding = 60;
    const scale = Math.min((width - padding * 2) / this.worldSize, (height - padding * 2) / this.worldSize);
    this.viewport.scale.set(scale);
    this.viewport.x = (width - this.worldSize * scale) / 2;
    this.viewport.y = (height - this.worldSize * scale) / 2;
  }

  /** Called after every camera change (pan/zoom): cheap flags only; the work happens in the frame. */
  private cameraChanged(): void {
    if (this.heavyWaits(performance.now())) {
      // mid-gesture: the transform already moved the whole scene; the rebuilds catch up a few times a second
      this.camDirty = true;
      this.requestRender();
      return;
    }
    this.syncCamera();
  }

  private heavyWaits(now: number): boolean { return this.gestureActive && now - this.lastHeavyAt < HEAVY_GAP_MS; }

  /** The heavy part of a camera change: level of detail, star / marker sizes, lanes and labels. */
  private syncCamera(): void {
    this.camDirty = false;
    this.lastHeavyAt = performance.now();
    const scale = this.viewport.scale.x;
    const next = lodFor(scale, this.lod);
    if (next !== this.lod) { this.lod = next; this.lanesDirty = true; this.applyAllStarScales(); this.applyAllMarkerScales(); }
    else if (Math.abs(scale - this.lastScaledAt) / Math.max(scale, 0.01) > 0.04) { this.applyAllStarScales(); this.applyAllMarkerScales(); this.lanesDirty = true; }
    this.labelsDirty = true;
    this.requestRender();
  }

  /** Current camera (screen position of the world origin + zoom); also read by the E2E gesture specs. */
  cameraState(): Camera { return { x: this.viewport.x, y: this.viewport.y, scale: this.viewport.scale.x }; }

  private applyCamera(cam: Camera): void {
    this.viewport.scale.set(cam.scale);
    this.viewport.x = cam.x;
    this.viewport.y = cam.y;
    this.cameraChanged();
  }

  private setGestureActive(active: boolean): void {
    this.gestureActive = active;
    if (!active && this.camDirty) this.syncCamera();
  }

  private onResize(): void {
    this.screenW = this.app.screen.width;
    this.screenH = this.app.screen.height;
    this.backdrop?.resize(this.screenW, this.screenH);
    this.cameraChanged();
  }

  // ---------------------------------------------------------------- data

  setData(systems: StarSystemClient[], lanes: StarLaneClient[]): void {
    if (this.destroyed) return;
    this.systems = systems;
    this.lanes = lanes;
    this.systemMap = new Map(systems.map(s => [s.id, s]));
    if (!this.initialized) return;
    if (!this.cameraFramed && systems.length > 0) { this.cameraFramed = true; this.fitGalaxyInView(); this.cameraChanged(); }
    this.rebuildNodes();
    this.renderRoute();
    this.renderFleets();
    this.requestRender();
  }

  setConstructionSystems(ids: string[]): void {
    this.constructionSystems = new Set(ids);
    if (!this.initialized) return;
    for (const node of this.nodes.values()) this.syncBuildBadge(node);
    this.requestRender();
  }

  setFleets(fleets: FleetClient[]): void {
    this.fleets = fleets;
    if (!this.initialized) return;
    this.renderFleets();
    this.requestRender();
  }

  /** Planned route of the selected fleet; ids of systems missing from the (known-only) map are skipped. */
  setPlannedRoute(path: string[] | null): void {
    this.routePath = path ?? [];
    if (this.initialized) { this.renderRoute(); this.requestRender(); }
  }

  get plannedRoute(): string[] {
    return this.routePath.filter(id => this.systemMap.has(id) || this.systems.some(s => s.id === id));
  }

  setSelectedSystem(id: string | null): void {
    this.selectedId = id;
    if (!this.initialized) return;
    this.updateSelection();
    this.lanesDirty = true;
    this.labelsDirty = true;
    this.requestRender();
  }

  // ---------------------------------------------------------------- stars

  private starScaleFor(node: StarNode): number {
    const st = starStyle(node.sys.starType);
    const scale = this.viewport.scale.x;
    const world = (st.radius * st.glowScale * 2 * LOD_STAR_MUL[this.lod]) / (STAR_TEX_R * 2);
    const minWorld = MIN_STAR_GLOW_PX / scale / (STAR_TEX_R * 2);
    return Math.max(world, minWorld);
  }

  private applyStarScale(node: StarNode): void {
    if (node.buildBadge) {
      const scale = this.viewport.scale.x;
      node.buildBadge.scale.set(1 / scale);
      node.buildBadge.position.set(node.sys.x + 11 / scale, node.sys.y - 12 / scale);
    }
    const unknown = node.state === 'unknown';
    if (unknown) {
      node.star.scale.set(Math.max(3.4 / 8, 5 / this.viewport.scale.x / 8));
      node.fogGlow?.scale.set(25 / 128 / this.viewport.scale.x);
      return;
    }
    const k = this.starScaleFor(node) * (node.sys.id === this.selectedId ? 1.25 : 1) * (node.state === 'detected' ? 0.8 : 1);
    node.star.scale.set(k);
    if (node.halo) node.halo.scale.set((this.haloRadiusWorld() * 2) / 128);
    if (node.ownerRing) node.ownerRing.scale.set(this.ringWorld(node, 1.9));
    if (node.homeRing) node.homeRing.scale.set(this.ringWorld(node, 2.7));
  }

  private ringWorld(node: StarNode, mul: number): number {
    const st = starStyle(node.sys.starType);
    return Math.max(st.radius * mul, 8 / this.viewport.scale.x) / 62;
  }

  private haloRadiusWorld(): number {
    // territory discs stay readable at every zoom: ~38 world units, never smaller than 26 screen px
    return Math.max(38, 26 / this.viewport.scale.x);
  }

  private applyAllStarScales(): void {
    this.lastScaledAt = this.viewport.scale.x;
    for (const n of this.nodes.values()) this.applyStarScale(n);
  }

  private rebuildNodes(): void {
    const seen = new Set<string>();
    this.fogCount = 0;
    this.grid.clear();
    for (const sys of this.systems) {
      seen.add(sys.id);
      let node = this.nodes.get(sys.id);
      const state = sys.discoveryStatus;
      const owner = sys.owner ? hexOf(sys.owner.civilizationColor, ACCENT.hex) : null;
      if (!node) {
        const star = new PIXI.Sprite(this.atlas.dot());
        star.anchor.set(0.5);
        this.starLayer.addChild(star);
        node = { sys, star, fogGlow: null, halo: null, ownerRing: null, homeRing: null, buildBadge: null, ownerColor: null, state: '', type: '' };
        this.nodes.set(sys.id, node);
      }
      node.sys = sys;
      node.star.position.set(sys.x, sys.y);
      if (node.state !== state || node.type !== sys.starType) {
        node.state = state; node.type = sys.starType;
        if (state === 'unknown') { node.star.texture = this.atlas.dot(); node.star.tint = 0x8292aa; node.star.alpha = 0.72; }
        else { node.star.texture = this.atlas.star(sys.starType); node.star.tint = 0xffffff; node.star.alpha = state === 'detected' ? 0.62 : 1; }
      }
      if (state === 'unknown') {
        this.fogCount++;
        if (this.quality !== 'low' && !node.fogGlow) {
          node.fogGlow = new PIXI.Sprite(this.atlas.glow());
          node.fogGlow.anchor.set(0.5);
          node.fogGlow.tint = 0x8292aa;
          this.haloLayer.addChild(node.fogGlow);
        }
        if (node.fogGlow) { node.fogGlow.position.set(sys.x, sys.y); node.fogGlow.alpha = 0.12; }
      } else if (node.fogGlow) { node.fogGlow.destroy(); node.fogGlow = null; }
      this.syncOwnership(node, owner, state);
      this.syncBuildBadge(node);
      this.applyStarScale(node);
      this.grid.insert({ x: sys.x, y: sys.y, id: sys.id });
    }
    for (const [id, n] of this.nodes) {
      if (seen.has(id)) continue;
      n.star.destroy(); n.fogGlow?.destroy(); n.halo?.destroy(); n.ownerRing?.destroy(); n.homeRing?.destroy(); n.buildBadge?.destroy();
      this.nodes.delete(id);
    }
    this.lanesDirty = true;
    this.labelsDirty = true;
    this.updateSelection();
  }

  private syncBuildBadge(node: StarNode): void {
    const show = node.state !== 'unknown' && this.constructionSystems.has(node.sys.id);
    if (!show) { node.buildBadge?.destroy(); node.buildBadge = null; return; }
    if (!node.buildBadge) {
      node.buildBadge = new PIXI.Text({ text: '⚒', style: { fontFamily: 'sans-serif', fontSize: 16, fill: 0xfbbf24, fontWeight: 'bold' } });
      node.buildBadge.anchor.set(0.5);
      this.markLayer.addChild(node.buildBadge);
    }
    this.applyStarScale(node);
  }

  private syncOwnership(node: StarNode, owner: number | null, state: string): void {
    const showOwner = owner !== null && state !== 'unknown';
    if (showOwner) {
      if (!node.halo && QUALITY_SETTINGS[this.quality].glowSprites) {
        node.halo = new PIXI.Sprite(this.atlas.territory());
        node.halo.anchor.set(0.5); node.halo.alpha = 0.85; node.halo.blendMode = 'add';
        this.haloLayer.addChild(node.halo);
      }
      if (!node.ownerRing) {
        node.ownerRing = new PIXI.Sprite(this.atlas.ring());
        node.ownerRing.anchor.set(0.5); node.ownerRing.alpha = 0.85;
        this.markLayer.addChild(node.ownerRing);
      }
      node.ownerColor = owner;
      node.halo?.position.set(node.sys.x, node.sys.y); if (node.halo) node.halo.tint = owner!;
      node.ownerRing.position.set(node.sys.x, node.sys.y); node.ownerRing.tint = owner!;
    } else {
      node.halo?.destroy(); node.halo = null;
      node.ownerRing?.destroy(); node.ownerRing = null;
      node.ownerColor = null;
    }
    if (node.sys.isHomeSystem && state !== 'unknown') {
      if (!node.homeRing) {
        node.homeRing = new PIXI.Sprite(this.atlas.ring());
        node.homeRing.anchor.set(0.5); node.homeRing.alpha = 0.5; node.homeRing.tint = ACCENT.hex;
        this.markLayer.addChild(node.homeRing);
      }
      node.homeRing.position.set(node.sys.x, node.sys.y);
    } else if (node.homeRing) { node.homeRing.destroy(); node.homeRing = null; }
  }

  // ---------------------------------------------------------------- lanes, route

  private drawLanes(): void {
    this.stats.laneRebuilds++;
    const g = this.lanesGfx;
    g.clear();
    this.faintGfx.clear();
    const sel = this.selectedId;
    const base = this.lod === 'galaxy' ? 0.26 : this.lod === 'region' ? 0.34 : 0.45;
    const scale = this.viewport.scale.x;
    const w = Math.max(0.7, 1.1 / Math.sqrt(scale)) ;
    const owned: Array<[StarSystemClient, StarSystemClient, number]> = [];
    const plain: Array<[StarSystemClient, StarSystemClient]> = [];
    /** both ends still unknown: public topology only, drawn very faint (Stellaris fog) */
    const faint: Array<[StarSystemClient, StarSystemClient]> = [];
    const touching: Array<[StarSystemClient, StarSystemClient]> = [];
    const touchingLanes: Array<{ a: StarSystemClient; b: StarSystemClient; distance: number }> = [];
    for (const lane of this.lanes) {
      const a = this.systemMap.get(lane.fromSystemId), b = this.systemMap.get(lane.toSystemId);
      if (!a || !b) continue;
      const ca = a.owner?.civilizationId, cb = b.owner?.civilizationId;
      if (sel && (a.id === sel || b.id === sel)) { touching.push([a, b]); touchingLanes.push({ a, b, distance: lane.distance }); }
      else if (ca && ca === cb) owned.push([a, b, hexOf(a.owner!.civilizationColor, ACCENT.hex)]);
      else if (a.discoveryStatus === 'unknown' && b.discoveryStatus === 'unknown') faint.push([a, b]);
      else plain.push([a, b]);
    }
    const path = (list: Array<[StarSystemClient, StarSystemClient]>) => { for (const [a, b] of list) g.moveTo(a.x, a.y).lineTo(b.x, b.y); };
    // unknown–unknown lanes: one thin, dim batch (hundreds of lanes stay cheap)
    // (a stroke without any path breaks Pixi's batcher, so every pass is guarded)
    if (faint.length) {
      const fg = this.faintGfx;
      for (const [a, b] of faint) fg.moveTo(a.x, a.y).lineTo(b.x, b.y);
      fg.stroke({ color: 0x6b7f9e, alpha: base * 0.34, width: w * 0.8 });
    }
    // soft glow pass, then the thin core
    if (plain.length) {
      path(plain); g.stroke({ color: 0x4a9eff, alpha: base * 0.28, width: w * 4.2 });
      path(plain); g.stroke({ color: 0x7fb6ff, alpha: base, width: w * 0.9 });
    }
    const byColor = new Map<number, Array<[StarSystemClient, StarSystemClient]>>();
    for (const [a, b, c] of owned) { const l = byColor.get(c); if (l) l.push([a, b]); else byColor.set(c, [[a, b]]); }
    for (const [color, list] of byColor) {
      path(list); g.stroke({ color, alpha: 0.2, width: w * 5 });
      path(list); g.stroke({ color, alpha: 0.75, width: w * 1.5 });
    }
    if (touching.length) {
      path(touching); g.stroke({ color: ACCENT.hex, alpha: 0.28, width: w * 5 });
      path(touching); g.stroke({ color: 0xcfe6ff, alpha: 0.9, width: w * 1.4 });
    }
    this.updateLaneLabels(touchingLanes, scale);
  }

  private updateLaneLabels(lanes: Array<{ a: StarSystemClient; b: StarSystemClient; distance: number }>, scale: number): void {
    let i = 0;
    for (const l of lanes) {
      let t = this.laneLabelPool[i];
      if (!t) {
        t = new PIXI.Text({ text: '', style: { fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fill: 0xcfe6ff, fontWeight: '700', stroke: { color: 0x030712, width: 3, join: 'round' } }, resolution: 2 });
        t.anchor.set(0.5, 0.5);
        this.laneLabelPool[i] = t;
        this.labelLayer.addChild(t);
      }
      i++;
      t.visible = true;
      const text = formatLy(l.distance);
      if (t.text !== text) t.text = text;
      t.scale.set(1 / scale);
      t.position.set((l.a.x + l.b.x) / 2, (l.a.y + l.b.y) / 2);
    }
    for (; i < this.laneLabelPool.length; i++) this.laneLabelPool[i]!.visible = false;
  }

  private renderRoute(): void {
    const g = this.routeGfx;
    g.clear();
    const points = this.plannedRoute.map(id => this.systemMap.get(id)).filter((s): s is StarSystemClient => !!s);
    if (points.length < 2) return;
    const scale = this.viewport.scale.x || 1;
    // Dashed polyline in world space, constant on-screen dash length, over a soft glow
    const dash = 6 / scale, gap = 4 / scale;
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i]!, b = points[i + 1]!;
      g.moveTo(a.x, a.y).lineTo(b.x, b.y);
    }
    g.stroke({ color: 0x00e5ff, alpha: 0.14, width: 7 / scale });
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i]!, b = points[i + 1]!;
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      if (len === 0) continue;
      const ux = (b.x - a.x) / len, uy = (b.y - a.y) / len;
      for (let d = 0; d < len; d += dash + gap) {
        const e = Math.min(len, d + dash);
        g.moveTo(a.x + ux * d, a.y + uy * d).lineTo(a.x + ux * e, a.y + uy * e);
      }
    }
    g.stroke({ color: 0x00e5ff, alpha: 0.9, width: 2 / scale });
    for (const p of points.slice(1)) g.circle(p.x, p.y, 4 / scale).fill({ color: 0x00e5ff, alpha: 0.9 });
  }

  // ---------------------------------------------------------------- selection

  private updateSelection(): void {
    const node = this.selectedId ? this.nodes.get(this.selectedId) : undefined;
    if (!node || node.state === 'unknown' && false) {
      this.selectLayer.visible = false;
      return;
    }
    if (!this.selectRing) {
      this.selectGlow = new PIXI.Sprite(this.atlas.glow(64)); this.selectGlow.anchor.set(0.5); this.selectGlow.tint = ACCENT.hex; this.selectGlow.alpha = 0.32; this.selectGlow.blendMode = 'add';
      this.selectRing = new PIXI.Sprite(this.atlas.ring()); this.selectRing.anchor.set(0.5); this.selectRing.tint = ACCENT.hex;
      this.selectBrackets = new PIXI.Sprite(this.atlas.brackets()); this.selectBrackets.anchor.set(0.5); this.selectBrackets.tint = 0xcfe6ff; this.selectBrackets.alpha = 0.8;
      this.selectLayer.addChild(this.selectGlow, this.selectRing, this.selectBrackets);
    }
    this.selectLayer.visible = true;
    this.positionSelection(0);
    this.applyAllStarScales();
  }

  private positionSelection(pulse: number): void {
    const node = this.selectedId ? this.nodes.get(this.selectedId) : undefined;
    if (!node || !this.selectRing || !this.selectGlow || !this.selectBrackets) return;
    const scale = this.viewport.scale.x;
    const px = Math.max(starStyle(node.sys.starType).radius * 2.4, 13 / scale);
    for (const s of [this.selectRing, this.selectGlow, this.selectBrackets]) s.position.set(node.sys.x, node.sys.y);
    this.selectRing.scale.set((px * (1 + pulse * 0.06)) / 62);
    this.selectGlow.scale.set((px * 2.6 * (1 + pulse * 0.1)) / 64);
    this.selectBrackets.scale.set((px * 1.5) / 58);
    this.selectGlow.alpha = 0.12 + pulse * 0.08;
  }

  // ---------------------------------------------------------------- fleets

  private markerScreenPx(): number { return LOD_SHIP_PX[this.lod]; }

  private applyMarkerScale(m: MarkerNode, sizeFactor: number): void {
    const scale = this.viewport.scale.x;
    const k = (this.markerScreenPx() * (0.55 + 0.45 * sizeFactor)) / SHIP_TEX_H / scale;
    m.ship.scale.set(k);
    if (m.badge) m.badge.scale.set(1 / scale);
    this.placeMarker(m);
  }

  /** Selection of own fleets (all selected ones get a ring; the last one is the primary the right-hand panel describes). */
  setSelectedFleets(ids: string[]): void {
    this.selectedFleetIds = new Set(ids);
    if (!this.initialized) return;
    this.renderFleets();
    this.dirty = true;
    this.requestRender();
  }

  /** Move mode: the next tap names the destination system; the cursor shows it on desktop. */
  setMoveMode(on: boolean): void {
    this.moveMode = on;
    if (this.initialized) (this.app.canvas as HTMLCanvasElement).style.cursor = on ? 'crosshair' : '';
  }

  /** Foreign fleets the server listed at the selected system (presence rule). Nothing else foreign is ever drawn. */
  setForeignFleets(systemId: string | null, list: ForeignFleetMarker[]): void {
    this.foreign = systemId && list.length ? { systemId, fleets: list } : null;
    if (!this.initialized) return;
    this.renderFleets();
    this.requestRender();
  }

  /** A far-out map folds several fleets into one counted stack; this one is spread out. */
  setExpandedStack(systemId: string | null): void {
    if (this.expandedStack === systemId) return;
    this.expandedStack = systemId;
    if (!this.initialized) return;
    this.renderFleets();
    this.requestRender();
  }

  private applyAllMarkerScales(): void {
    for (const m of this.markers.values()) this.applyMarkerScale(m, m.sizeFactor);
    for (const t of this.travelers.values()) this.applyTravelScale(t);
    this.travelLines.alpha = 1;
    this.drawFleetFx();
  }

  private damageOf(fleets: FleetClient[]): number {
    let cur = 0, max = 0;
    for (const f of fleets) for (const s of f.ships) { cur += s.currentHull ?? 0; max += s.maxHull ?? 0; }
    return max > 0 ? 1 - cur / max : 0;
  }

  private upsertMarker(key: string, kind: HitKind, fleetId: string | null, systemId: string, slot: Offset, keep: Set<string>): MarkerNode {
    keep.add(key);
    let m = this.markers.get(key);
    if (!m) {
      const container = new PIXI.Container();
      const ship = new PIXI.Sprite(); ship.anchor.set(0.5);
      container.addChild(ship);
      this.fleetLayer.addChild(container);
      m = { container, ship, trail: null, badge: null, key, kind, fleetId, systemId, slot, sizeFactor: 1 };
      this.markers.set(key, m);
    }
    m.kind = kind; m.fleetId = fleetId; m.systemId = systemId; m.slot = slot;
    return m;
  }

  /**
   * Resting fleets: every fleet is its own marker (own fleets in the category colour; foreign ones the server showed, in their
   * civilization's colour), fanned out around the star so each can be tapped separately. Many fleets, or a far zoom level,
   * fold into one counted stack that opens on tap. Foreign fleets exist here only for the selected system and only as far
   * as the server listed them (presence rule): nothing is drawn for fleets the player may not see.
   */
  private renderFleets(): void {
    const byId = new Map(this.fleets.map(f => [f.id, f]));
    this.fleetById = byId;
    const keep = new Set<string>();
    const groups = fleetsBySystem(this.fleets);
    const systemIds = new Set<string>(groups.keys());
    if (this.foreign) systemIds.add(this.foreign.systemId);
    const far = this.lod === 'galaxy';
    for (const systemId of systemIds) {
      const system = this.systemMap.get(systemId);
      if (!system) continue;
      const own = (groups.get(systemId)?.ids ?? []).map(id => byId.get(id)!).filter(Boolean);
      const alien = this.foreign && this.foreign.systemId === systemId ? this.foreign.fleets : [];
      const total = own.length + alien.length;
      if (total === 0) continue;
      const hasSelected = own.some(f => this.selectedFleetIds.has(f.id));
      if (shouldStack(total, far, this.expandedStack === systemId || hasSelected)) {
        const cats = new Set(own.map(f => categoryOf(f)));
        const cat: FleetCategory = own.length === 0 ? 'mixed' : cats.size === 1 ? [...cats][0]! : 'mixed';
        const counts: Record<string, number> = {};
        for (const f of own) for (const [c, n] of Object.entries(f.stats.shipCounts)) counts[c] = (counts[c] ?? 0) + n;
        const cls = representativeClass(counts) ?? 'scout';
        const role = CATEGORY_ROLE[cat];
        const m = this.upsertMarker(`s:${systemId}`, 'stack', null, systemId, fanSlots(1)[0]!, keep);
        m.ship.texture = this.atlas.ship(cls, role, this.damageOf(own));
        m.ship.alpha = 1; m.ship.tint = 0xd5ddeb;
        m.sizeFactor = designOf(cls).sizeFactor / 1.45 + 0.4;
        m.container.position.set(system.x, system.y);
        if (!m.badge) m.badge = this.takeBadge();
        this.fillBadge(m.badge, String(total), ROLE_COLORS[role].hex);
        m.container.addChild(m.badge);
        this.applyMarkerScale(m, m.sizeFactor);
        continue;
      }
      const slots = fanSlots(total);
      let i = 0;
      for (const f of own) {
        const cat = categoryOf(f);
        const role = CATEGORY_ROLE[cat];
        const cls = representativeClass(f.stats.shipCounts) ?? 'scout';
        const m = this.upsertMarker(`f:${f.id}`, 'fleet', f.id, systemId, slots[i++]!, keep);
        m.ship.texture = this.atlas.ship(cls, role, this.damageOf([f]));
        m.ship.alpha = f.status === 'surveying' ? 0.7 : 1;
        m.ship.tint = this.selectedFleetIds.has(f.id) ? 0xffffff : 0xd5ddeb;
        m.sizeFactor = designOf(cls).sizeFactor / 1.45 + 0.4;
        m.container.position.set(system.x, system.y);
        if (m.badge) { this.releaseBadge(m.badge); m.badge = null; }
        this.applyMarkerScale(m, m.sizeFactor);
      }
      for (const a of alien) {
        const cls = representativeClass(a.shipCounts) ?? 'scout';
        const m = this.upsertMarker(`x:${a.id}`, 'foreign', a.id, systemId, slots[i++]!, keep);
        m.ship.texture = this.atlas.ship(cls, 'military', 0);
        m.ship.alpha = 1; m.ship.tint = a.color;
        m.sizeFactor = designOf(cls).sizeFactor / 1.45 + 0.4;
        m.container.position.set(system.x, system.y);
        if (m.badge) { this.releaseBadge(m.badge); m.badge = null; }
        this.applyMarkerScale(m, m.sizeFactor);
      }
    }
    for (const [key, m] of this.markers) {
      if (keep.has(key)) continue;
      if (m.badge) this.releaseBadge(m.badge);
      m.container.destroy({ children: true }); this.markers.delete(key);
    }
    this.renderTravelers();
    this.drawFleetFx();
    this.updateFleetLabels(Date.now());
  }

  private placeMarker(m: MarkerNode): void {
    const scale = this.viewport.scale.x;
    m.ship.position.set(m.slot.dx / scale, m.slot.dy / scale);
    if (m.badge) m.badge.position.set((m.slot.dx + 10) / scale, (m.slot.dy + 10) / scale);
  }

  /** Rings of the selected (and hovered) fleets, and the lane the selected traveling fleets are flying along. */
  private drawFleetFx(): void {
    const g = this.fxGfx;
    g.clear();
    const scale = this.viewport.scale.x;
    for (const m of this.markers.values()) {
      const sel = m.fleetId !== null && m.kind === 'fleet' && this.selectedFleetIds.has(m.fleetId);
      const hov = m.key === this.hoverKey;
      if (!sel && !hov) continue;
      g.circle(m.container.x + m.slot.dx / scale, m.container.y + m.slot.dy / scale, 17 / scale)
        .stroke({ color: sel ? 0x7dffcf : 0xffffff, width: (sel ? 2.2 : 1.2) / scale, alpha: sel ? 0.95 : 0.55 });
    }
    for (const t of this.travelers.values()) {
      const sel = this.selectedFleetIds.has(t.fleetId);
      const hov = `t:${t.fleetId}` === this.hoverKey;
      if (!sel && !hov) continue;
      if (sel) g.moveTo(t.from.x, t.from.y).lineTo(t.to.x, t.to.y).stroke({ color: 0x7dffcf, width: 2.4 / scale, alpha: 0.75 });
      g.circle(t.container.x, t.container.y, 17 / scale).stroke({ color: sel ? 0x7dffcf : 0xffffff, width: (sel ? 2.2 : 1.2) / scale, alpha: sel ? 0.95 : 0.55 });
    }
  }

  private shortName(name: string): string { return name.length > 13 ? `${name.slice(0, 12)}…` : name; }

  /** Names under the markers: at the closest zoom for every fleet, otherwise only for the selected / hovered ones; ETA on travelers. */
  private updateFleetLabels(now: number): void {
    this.lastFleetLabelAt = performance.now();
    const scale = this.viewport.scale.x;
    const showAll = this.lod === 'close';
    const used = new Set<string>();
    const put = (key: string, text: string, x: number, y: number, color: number) => {
      if (used.size >= 60) return;
      used.add(key);
      let t = this.fleetLabels.get(key);
      if (!t) {
        t = new PIXI.Text({ text, style: { fontSize: 10, fill: color, fontFamily: 'Inter, sans-serif', fontWeight: '600', stroke: { color: 0x030712, width: 3, join: 'round' } }, resolution: 2 });
        t.anchor.set(0.5, 0);
        this.fleetLabelLayer.addChild(t);
        this.fleetLabels.set(key, t);
      }
      if (t.text !== text) t.text = text;
      t.style.fill = color;
      t.position.set(x, y);
      t.scale.set(1 / scale);
    };
    for (const m of this.markers.values()) {
      if (m.kind === 'stack' || !m.fleetId) continue;
      const sel = this.selectedFleetIds.has(m.fleetId);
      if (!showAll && !sel && m.key !== this.hoverKey) continue;
      const name = m.kind === 'foreign' ? (this.foreign?.fleets.find(a => a.id === m.fleetId)?.name ?? '') : (this.fleetById.get(m.fleetId)?.name ?? '');
      put(m.key, this.shortName(name), m.container.x + m.slot.dx / scale, m.container.y + (m.slot.dy + 14) / scale, sel ? 0x7dffcf : 0xcbd5e1);
    }
    for (const t of this.travelers.values()) {
      const sel = this.selectedFleetIds.has(t.fleetId);
      if (!showAll && !sel && `t:${t.fleetId}` !== this.hoverKey) continue;
      const f = this.fleetById.get(t.fleetId);
      put(`t:${t.fleetId}`, `${this.shortName(f?.name ?? '')} · ${formatDuration(Math.max(0, t.end - now))}`, t.container.x, t.container.y + 16 / scale, sel ? 0x7dffcf : 0xcbd5e1);
    }
    for (const [key, t] of this.fleetLabels) {
      if (used.has(key)) continue;
      t.destroy(); this.fleetLabels.delete(key);
    }
  }

  private takeBadge(): PIXI.Container {
    const c = this.badgePool.pop() ?? (() => {
      const cont = new PIXI.Container();
      const bg = new PIXI.Graphics(); bg.label = 'bg';
      const t = new PIXI.Text({ text: '', style: { fontFamily: 'JetBrains Mono, monospace', fontSize: 10, fill: 0xffffff, fontWeight: '700' }, resolution: 2 });
      t.anchor.set(0.5); t.label = 'txt';
      cont.addChild(bg, t);
      return cont;
    })();
    c.visible = true;
    return c;
  }

  private fillBadge(c: PIXI.Container, text: string, color: number): void {
    const bg = c.getChildByLabel('bg') as PIXI.Graphics, t = c.getChildByLabel('txt') as PIXI.Text;
    t.text = text;
    const w = Math.max(14, t.width + 8);
    bg.clear().roundRect(-w / 2, -8, w, 16, 8).fill({ color: 0x0b1020, alpha: 0.9 }).stroke({ color, width: 1.2, alpha: 0.95 });
  }

  private releaseBadge(c: PIXI.Container): void {
    c.parent?.removeChild(c); c.visible = false; this.badgePool.push(c);
  }

  private renderTravelers(): void {
    const keep = new Set<string>();
    this.travelLines.clear();
    const scale = this.viewport.scale.x;
    for (const fleet of this.fleets) {
      if (fleet.status !== 'traveling') continue;
      const order = fleet.activeOrder;
      if (!order?.fromSystemId || !order?.toSystemId || !order?.issuedAt || !order?.arrivesAt) continue;
      const from = this.systemMap.get(order.fromSystemId), to = this.systemMap.get(order.toSystemId);
      if (!from || !to) continue;
      keep.add(fleet.id);
      const cat = categoryOf(fleet);
      const role = CATEGORY_ROLE[cat];
      const hex = ROLE_COLORS[role].hex;
      // the route being flown: faint glow + thin line
      this.travelLines.moveTo(from.x, from.y).lineTo(to.x, to.y);
      this.travelLines.stroke({ color: hex, alpha: 0.28, width: 0.8 / Math.sqrt(scale) + 0.4 });
      let t = this.travelers.get(fleet.id);
      if (!t) {
        const container = new PIXI.Container();
        const trail = new PIXI.Sprite(this.atlas.flame()); trail.anchor.set(0.5, 0); trail.blendMode = 'add';
        const ship = new PIXI.Sprite(); ship.anchor.set(0.5);
        container.addChild(trail, ship);
        this.travelLayer.addChild(container);
        t = { fleetId: fleet.id, container, ship, trail, from: { x: 0, y: 0 }, to: { x: 0, y: 0 }, start: 0, end: 0 };
        this.travelers.set(fleet.id, t);
      }
      const cls = representativeClass(fleet.stats.shipCounts) ?? 'scout';
      t.ship.texture = this.atlas.ship(cls, role, this.damageOf([fleet]));
      t.ship.tint = this.selectedFleetIds.has(fleet.id) ? 0xffffff : 0xd5ddeb;
      t.trail.tint = designOf(cls).flame.color;
      (t.ship as unknown as { _sf?: number })._sf = designOf(cls).sizeFactor / 1.45 + 0.4;
      t.from = { x: from.x, y: from.y }; t.to = { x: to.x, y: to.y };
      t.start = new Date(order.issuedAt).getTime(); t.end = new Date(order.arrivesAt).getTime();
      t.container.rotation = Math.atan2(to.y - from.y, to.x - from.x) + Math.PI / 2;
      this.applyTravelScale(t);
      this.updateTravelPosition(t, Date.now());
    }
    for (const [id, t] of this.travelers) {
      if (keep.has(id)) continue;
      t.container.destroy({ children: true }); this.travelers.delete(id);
    }
  }

  private applyTravelScale(t: TravelNode): void {
    const scale = this.viewport.scale.x;
    const sf = (t.ship as unknown as { _sf?: number })._sf ?? 1;
    const k = (this.markerScreenPx() * (0.55 + 0.45 * sf)) / SHIP_TEX_H / scale;
    t.ship.scale.set(k);
    // exhaust trail behind the ship (the texture is 128 px tall, pointing away from the nose)
    t.trail.scale.set(k * 0.55, k * 0.5);
    t.trail.position.set(0, SHIP_TEX_H * k * 0.28);
  }

  private updateTravelPosition(t: TravelNode, now: number): void {
    const p = Math.min(1, Math.max(0, (now - t.start) / Math.max(1, t.end - t.start)));
    t.container.position.set(t.from.x + (t.to.x - t.from.x) * p, t.from.y + (t.to.y - t.from.y) * p);
    if (!this.reduced) {
      const flick = 0.85 + 0.15 * Math.sin(now / 55 + t.start);
      t.trail.alpha = 0.55 * flick;
    } else t.trail.alpha = 0.5;
  }

  /** Test/diagnostic view of the fleet markers (what the player sees on the map). */
  fleetMarkers(): { resting: Array<{ systemId: string; count: number; categories: FleetCategory[] }>; traveling: Array<{ fleetId: string; category: FleetCategory }> } {
    const known = new Set(this.systems.map(s => s.id));
    return {
      resting: [...fleetsBySystem(this.fleets)].filter(([id]) => known.has(id)).map(([systemId, g]) => ({ systemId, count: g.count, categories: g.categories })),
      traveling: this.fleets.filter(f => f.status === 'traveling').map(f => ({ fleetId: f.id, category: categoryOf(f) })),
    };
  }

  /** Test/diagnostic: where every tappable fleet marker is on the screen (CSS px relative to the canvas). */
  fleetMarkerPositions(): Array<{ key: string; kind: HitKind; id: string; systemId: string; x: number; y: number }> {
    return this.hitTargets().map(t => ({ key: `${t.kind}:${t.id}`, kind: t.kind, id: t.id, systemId: t.systemId, x: t.x, y: t.y }));
  }

  /** Counts of what is currently on the display list (performance diagnostics). */
  displayStats(): { stars: number; visibleLabels: number; markers: number; travelers: number; lod: Lod; quality: Quality; backdropCells: number } {
    return { stars: this.nodes.size, visibleLabels: this.labelPool.filter(l => l.visible).length, markers: this.markers.size, travelers: this.travelers.size, lod: this.lod, quality: this.quality, backdropCells: this.backdrop?.cellCount ?? 0 };
  }

  // ---------------------------------------------------------------- labels

  private takeLabel(i: number): PIXI.Text {
    let t = this.labelPool[i];
    if (!t) {
      t = new PIXI.Text({ text: '', style: { fontSize: 11, fill: 0x94a3b8, fontFamily: 'Inter, sans-serif', stroke: { color: 0x030712, width: 3, join: 'round' } }, resolution: 2 });
      t.anchor.set(0.5, 0);
      this.labelPool[i] = t;
      this.labelLayer.addChild(t);
    }
    return t;
  }

  private updateLabels(): void {
    this.stats.labelRebuilds++;
    const scale = this.viewport.scale.x;
    const view = visibleWorldRect({ x: this.viewport.x, y: this.viewport.y, scale }, this.screenW, this.screenH);
    const max = QUALITY_SETTINGS[this.quality].maxLabels;
    type C = { n: StarNode; prio: number; d: number };
    const cands: C[] = [];
    const cx = (view.x0 + view.x1) / 2, cy = (view.y0 + view.y1) / 2;
    for (const n of this.nodes.values()) {
      if (n.state === 'unknown' || !inRect(n.sys.x, n.sys.y, view, 20)) continue;
      const selected = n.sys.id === this.selectedId;
      const important = selected || n.sys.isHomeSystem || !!n.sys.owner;
      if (this.lod === 'galaxy' && !important) continue;
      cands.push({ n, prio: selected ? 0 : n.sys.isHomeSystem ? 1 : n.sys.owner ? 2 : 3, d: (n.sys.x - cx) ** 2 + (n.sys.y - cy) ** 2 });
    }
    cands.sort((a, b) => a.prio - b.prio || a.d - b.d);
    // greedy placement in screen space: a label that would overlap an already placed one is skipped
    const placed: Array<[number, number, number, number]> = [];
    const shown: StarNode[] = [];
    const fontScale = this.lod === 'close' ? 1.15 : 1;
    for (const { n } of cands.slice(0, 700)) {
      if (shown.length >= max) break;
      const sx = this.viewport.x + n.sys.x * scale;
      const sy = this.viewport.y + n.sys.y * scale + Math.max(starStyle(n.sys.starType).radius * 1.7 * scale, 9);
      const w = n.sys.name.length * 5.9 * fontScale + 6, h = 14 * fontScale;
      const x0 = sx - w / 2, x1 = sx + w / 2, y0 = sy, y1 = sy + h;
      const selected = n.sys.id === this.selectedId;
      if (!selected && placed.some(r => x0 < r[2] && x1 > r[0] && y0 < r[3] && y1 > r[1])) continue;
      placed.push([x0, y0, x1, y1]);
      shown.push(n);
    }
    let i = 0;
    for (const n of shown) {
      const t = this.takeLabel(i++);
      const selected = n.sys.id === this.selectedId;
      t.visible = true;
      if (t.text !== n.sys.name) t.text = n.sys.name;
      t.style.fill = selected ? 0xcfe6ff : n.state === 'detected' ? 0x64748b : n.sys.owner ? 0xe2e8f0 : 0x94a3b8;
      t.scale.set(fontScale / scale);
      const offset = Math.max(starStyle(n.sys.starType).radius * 1.7, 9 / scale);
      t.position.set(n.sys.x, n.sys.y + offset);
    }
    for (; i < this.labelPool.length; i++) this.labelPool[i]!.visible = false;
  }

  // ---------------------------------------------------------------- loop

  /** Brief highlight of a just-revealed system (a static ring under prefers-reduced-motion). */
  flashSystem(systemId: string): void {
    const sys = this.systemMap.get(systemId);
    if (!sys || !this.initialized || this.destroyed) return;
    const sprite = new PIXI.Sprite(this.atlas.ring());
    sprite.anchor.set(0.5); sprite.position.set(sys.x, sys.y); sprite.tint = 0x7dffcf; sprite.blendMode = 'add';
    this.selectLayer.addChild(sprite);
    this.flashes.push({ sprite, at: performance.now(), x: sys.x, y: sys.y });
    this.requestRender();
  }

  private stepFlashes(now: number): void {
    const scale = this.viewport.scale.x;
    for (let i = this.flashes.length - 1; i >= 0; i--) {
      const f = this.flashes[i]!;
      const t = (now - f.at) / 1800;
      if (t >= 1) { f.sprite.destroy(); this.flashes.splice(i, 1); continue; }
      const k = this.reduced ? 1 : 0.6 + 2.2 * t;
      f.sprite.scale.set((Math.max(28 / scale, 26) / 62) * k);
      f.sprite.alpha = this.reduced ? 0.8 : 0.9 * (1 - t);
    }
    this.dirty = true;
  }

  private animating(): boolean {
    return this.flashes.length > 0 || this.travelers.size > 0 || (!this.reduced && (!!this.selectedId || this.fogCount > 0));
  }

  private requestRender(): void {
    this.dirty = true;
    if (this.raf === null && !this.destroyed && this.initialized) this.raf = requestAnimationFrame(this.frame);
  }

  private frame = (now: number): void => {
    this.raf = null;
    if (this.destroyed) return;
    this.stats.frames++;
    if (this.camDirty && !this.heavyWaits(now)) this.syncCamera();
    if (this.lanesDirty) { this.lanesDirty = false; this.drawLanes(); this.renderRoute(); this.dirty = true; }
    if (this.labelsDirty && now - this.lastLabelAt > 90) { this.labelsDirty = false; this.lastLabelAt = now; this.updateLabels(); this.updateFleetLabels(Date.now()); this.dirty = true; }
    if (this.flashes.length) this.stepFlashes(now);
    if (this.travelers.size) {
      const t = Date.now();
      for (const tr of this.travelers.values()) this.updateTravelPosition(tr, t);
      if (this.selectedFleetIds.size || this.hoverKey) this.drawFleetFx();
      if (performance.now() - this.lastFleetLabelAt > 500) this.updateFleetLabels(t);
      this.dirty = true;
    }
    if (!this.reduced && this.selectedId && now - this.lastPulseAt > 42) {
      this.lastPulseAt = now;
      this.positionSelection(0.5 + 0.5 * Math.sin((now / 2400) * Math.PI * 2));
      this.dirty = true;
    }
    if (!this.reduced && this.fogCount && now - this.lastFogAt > 90) {
      this.lastFogAt = now;
      const view = visibleWorldRect({ x: this.viewport.x, y: this.viewport.y, scale: this.viewport.scale.x }, this.screenW, this.screenH);
      for (const node of this.nodes.values()) {
        if (node.state !== 'unknown' || !inRect(node.sys.x, node.sys.y, view, 12)) continue;
        const phase = now / 1350 + node.sys.x * 0.017 + node.sys.y * 0.011;
        if (node.fogGlow) node.fogGlow.alpha = 0.10 + 0.06 * Math.sin(phase);
        node.star.alpha = 0.65 + 0.16 * Math.sin(phase + 0.7);
      }
      this.dirty = true;
    }
    this.backdrop.update({ x: this.viewport.x, y: this.viewport.y, scale: this.viewport.scale.x }, this.screenW, this.screenH);
    if (this.dirty) { this.dirty = false; this.renderNow(); }
    if (this.animating() || this.dirty || this.camDirty || this.labelsDirty || this.lanesDirty) this.raf = requestAnimationFrame(this.frame);
  };

  private renderNow(): void {
    if (!this.initialized || this.destroyed) return;
    this.stats.renders++;
    this.app.render();
  }

  // ---------------------------------------------------------------- interaction

  private setupInteraction(): void {
    // The map is driven by native pointer events on the canvas (not Pixi's federated events): tracking starts at
    // pointerdown, the pointer is captured so a drag that leaves the canvas still ends, and pointercancel is handled.
    this.app.stage.eventMode = 'none';
    const canvas = this.app.canvas as HTMLCanvasElement;
    const pos = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const on = <K extends keyof HTMLElementEventMap>(el: HTMLElement | Window, type: K | string, fn: (e: never) => void, opts?: AddEventListenerOptions) => {
      el.addEventListener(type, fn as EventListener, opts);
      this.domCleanup.push(() => el.removeEventListener(type, fn as EventListener, opts));
    };

    on(canvas, 'pointerdown', (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      try { canvas.setPointerCapture(e.pointerId); } catch { /* synthetic pointer: no capture, move events still arrive */ }
      if (e.pointerType === 'mouse' && e.shiftKey && !this.gestures.active) {
        // Shift + click adds a fleet to the selection, Shift + drag draws a selection box: neither pans the map
        const p = pos(e);
        this.box = { id: e.pointerId, ax: p.x, ay: p.y, moved: false };
        return;
      }
      this.tapAdditive = e.pointerType === 'mouse' && (e.ctrlKey || e.metaKey);
      this.gestures.pointerDown(e.pointerId, pos(e), performance.now());
    });
    on(canvas, 'pointermove', (e: PointerEvent) => {
      if (this.box && this.box.id === e.pointerId) {
        const p = pos(e);
        if (!this.box.moved && Math.hypot(p.x - this.box.ax, p.y - this.box.ay) > 7) this.box.moved = true;
        if (this.box.moved) {
          const r = rectFrom(this.box.ax, this.box.ay, p.x, p.y);
          this.boxGfx.clear().rect(r.x0, r.y0, r.x1 - r.x0, r.y1 - r.y0).fill({ color: 0x7dffcf, alpha: 0.08 }).stroke({ color: 0x7dffcf, width: 1, alpha: 0.8 });
          this.dirty = true; this.requestRender();
        }
        return;
      }
      // hover (mouse without a button): tooltip + highlight of the fleet under the cursor
      if (e.pointerType === 'mouse' && e.buttons === 0) { const hp = pos(e); this.hoverAt(hp.x, hp.y); return; }
      // coalesced events: the last position of each pointer is all the camera needs
      this.gestures.pointerMove(e.pointerId, pos(e));
    });
    on(canvas, 'pointerup', (e: PointerEvent) => {
      if (this.box && this.box.id === e.pointerId) {
        const b = this.box; this.box = null;
        this.boxGfx.clear(); this.dirty = true; this.requestRender();
        const p = pos(e);
        if (!b.moved) this.tapAt(b.ax, b.ay, true);
        else this.callbacks.onBoxSelect?.(fleetsInRect(this.hitTargets(), rectFrom(b.ax, b.ay, p.x, p.y)), e.ctrlKey || e.metaKey);
        try { canvas.releasePointerCapture(e.pointerId); } catch { /* already released */ }
        return;
      }
      this.gestures.pointerUp(e.pointerId, pos(e), performance.now());
      try { canvas.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    });
    on(canvas, 'pointerleave', (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && this.hoverKey) { this.hoverKey = null; this.drawFleetFx(); this.updateFleetLabels(Date.now()); this.requestRender(); this.callbacks.onHover?.(null); }
    });
    const cancel = () => { this.box = null; this.boxGfx.clear(); this.gestures.cancel(); };
    on(canvas, 'pointercancel', cancel);
    on(window, 'blur', cancel);
    on(document as unknown as HTMLElement, 'visibilitychange', cancel);
    // long-press menu, Safari's own pinch gesture and double-tap zoom must never fight the map
    on(canvas, 'contextmenu', (e: MouseEvent) => {
      e.preventDefault();
      // right mouse button on a star: the Stellaris-style move command for the selected fleets
      if (e.button !== 2) return;
      const r = canvas.getBoundingClientRect();
      const { target, star } = this.pickAt(e.clientX - r.left, e.clientY - r.top);
      const id = target && !this.travelers.has(target.id) ? target.systemId : star?.id;
      if (id) this.callbacks.onContextTarget?.(id);
    });
    for (const g of ['gesturestart', 'gesturechange', 'gestureend']) on(canvas, g, (e: Event) => e.preventDefault(), { passive: false });

    // Wheel zoom (mouse / trackpad) toward the cursor
    on(canvas, 'wheel', (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 0.9 : 1.1;
      const r = canvas.getBoundingClientRect();
      const cam = this.cameraState();
      this.applyCamera(zoomAt(cam, { x: e.clientX - r.left, y: e.clientY - r.top }, cam.scale * factor, MIN_SCALE, MAX_SCALE));
    }, { passive: false });
  }

  /** Every tappable thing on the screen right now, in CSS px relative to the canvas: fleet markers, stacks, traveling fleets. */
  private hitTargets(): HitTarget[] {
    const scale = this.viewport.scale.x, vx = this.viewport.x, vy = this.viewport.y;
    const out: HitTarget[] = [];
    for (const m of this.markers.values()) {
      out.push({ kind: m.kind, id: m.kind === 'stack' ? m.systemId : m.fleetId!, systemId: m.systemId, x: vx + m.container.x * scale + m.slot.dx, y: vy + m.container.y * scale + m.slot.dy });
    }
    for (const t of this.travelers.values()) {
      const f = this.fleetById.get(t.fleetId);
      out.push({ kind: 'fleet', id: t.fleetId, systemId: f?.currentSystemId ?? '', x: vx + t.container.x * scale, y: vy + t.container.y * scale });
    }
    return out;
  }

  /** The nearest thing under a point: a fleet marker / stack, or a star; the nearest wins so a marker next to a star never hides it. */
  private pickAt(sx: number, sy: number): { target: HitTarget | null; star: { id: string } | null } {
    const scale = this.viewport.scale.x;
    const wx = (sx - this.viewport.x) / scale, wy = (sy - this.viewport.y) / scale;
    const star = this.grid.nearest(wx, wy, FLEET_HIT_RADIUS / scale);
    const target = hitTest(this.hitTargets(), sx, sy);
    if (target && star) {
      const ds = Math.hypot(this.viewport.x + star.x * scale - sx, this.viewport.y + star.y * scale - sy);
      const dt = Math.hypot(target.x - sx, target.y - sy);
      if (ds < dt) return { target: null, star };
    }
    return { target, star };
  }

  /**
   * A tap picks the nearest thing within a finger-sized radius (22 px → 44 px target) regardless of zoom:
   * a fleet marker, a counted stack, or a star. In move mode every tap names a destination system instead.
   */
  private tapAt(sx: number, sy: number, additive = false): void {
    const { target, star } = this.pickAt(sx, sy);
    if (this.moveMode) {
      const dest = target && !this.travelers.has(target.id) ? target.systemId : star?.id;
      if (dest) this.callbacks.onSelectSystem(dest); else this.callbacks.onEmptyTap?.();
      return;
    }
    if (target) {
      if (target.kind === 'stack') this.callbacks.onStackTap?.(target.systemId);
      else if (target.kind === 'foreign') this.callbacks.onForeignTap?.(target.id, target.systemId);
      else this.callbacks.onFleetTap?.(target.id, target.systemId, additive);
      return;
    }
    if (star) { this.callbacks.onSelectSystem(star.id); return; }
    this.callbacks.onEmptyTap?.();
  }

  private hoverAt(sx: number, sy: number): void {
    const { target } = this.pickAt(sx, sy);
    const key = target ? (this.travelers.has(target.id) && target.kind === 'fleet' ? `t:${target.id}` : target.kind === 'stack' ? `s:${target.systemId}` : `${target.kind === 'foreign' ? 'x' : 'f'}:${target.id}`) : null;
    if (key === this.hoverKey) return;
    this.hoverKey = key;
    (this.app.canvas as HTMLCanvasElement).style.cursor = this.moveMode ? 'crosshair' : target ? 'pointer' : '';
    this.drawFleetFx();
    this.updateFleetLabels(Date.now());
    this.requestRender();
    this.callbacks.onHover?.(target && target.kind !== 'stack' ? { fleetId: target.id, kind: target.kind, x: sx, y: sy } : null);
  }


  resize(): void {
    // resizeTo: container handles auto-resize via ResizeObserver; manual resize not needed
  }

  isDestroyed(): boolean {
    return this.destroyed;
  }

  async snapshot(maxSide: number): Promise<string | null> {
    if (!this.initialized || this.destroyed) return null;
    const src = this.app.renderer.extract.canvas({ target: this.app.stage, resolution: 1 }) as HTMLCanvasElement;
    const k = Math.min(1, maxSide / Math.max(src.width, src.height, 1));
    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(src.width * k));
    out.height = Math.max(1, Math.round(src.height * k));
    out.getContext('2d')?.drawImage(src, 0, 0, out.width, out.height);
    return out.toDataURL('image/png');
  }

  destroy(): void {
    if (currentMap === this) currentMap = null;
    this.destroyed = true;
    this.flashes = [];
    if (this.raf !== null) { cancelAnimationFrame(this.raf); this.raf = null; }
    if (!this.initialized) return;
    for (const off of this.domCleanup) off();
    this.domCleanup = [];
    const canvas = this.app.canvas as HTMLCanvasElement;
    if (canvas.parentElement) canvas.parentElement.removeChild(canvas);
    try { this.backdrop.destroy(); this.atlas.destroy(); } catch { /* ignore */ }
    try { this.app.destroy(true); } catch { /* ignore */ }
  }
}

// A test/diagnostic handle for sizes of the shared art (kept here so the map owns its own constants).
export const MAP_ART = { shipTexHeight: SHIP_TEX_H } satisfies Record<string, number>;
export type { ShipClass };
